/**
 * The farm log, read for the two panels on the auction screen (13a): who holds what now, and what
 * every past sale returned.
 *
 * `active` in the log means the player is still a live farm holding — he has not graduated to the
 * fantasy roster, been written off, or run out of college eligibility. That flag is the roster;
 * everything else is history.
 */

import farmLog from '../data/farm-log.json';
import type { FarmNomination } from '../store/types.js';

export interface FarmRow {
  farmYear: number;
  gm: string;
  player: string;
  pos?: string;
  school?: string;
  college?: string;
  bid?: number | null;
  outcome: string;
  status?: string;
  statusNote?: string;
  /** The fantasy round he graduated into, where he did. */
  lgRound?: number | null;
  active?: boolean;
}

export const FARM_SALES: FarmRow[] = (farmLog as unknown as FarmRow[])
  .filter((r) => r.outcome === 'won');

export type OutcomeTone = 'good' | 'bad' | 'open' | 'plain';

/** What became of him, in the drawing's own five words or fewer. */
export function outcomeOf(r: FarmRow): { text: string; tone: OutcomeTone } {
  if (r.lgRound) return { text: `graduated · rd ${r.lgRound}`, tone: 'good' };
  if (r.status === 'ended') return { text: 'forfeited', tone: 'bad' };
  if (r.active) return { text: 'held', tone: 'open' };
  if (r.status === 'college') return { text: 'still in college', tone: 'open' };
  return { text: 'reached the NFL', tone: 'plain' };
}

/** Live holdings per GM, newest purchase first. */
export function farmRosters(gms: readonly string[]): Array<{ gm: string; held: FarmRow[] }> {
  return gms.map((gm) => ({
    gm,
    held: FARM_SALES
      .filter((r) => r.active && r.gm.toUpperCase() === gm.toUpperCase())
      .sort((a, b) => b.farmYear - a.farmYear),
  }));
}

/**
 * Tonight's sales in the shape the history panels read.
 *
 * TONIGHT IS HISTORY THE MOMENT THE HAMMER FALLS (Tim, 9 September: "all the picks have been made
 * in farm. need a main draft-live mark as completed option to lock everything in and write to
 * history/past auctions"). `farm-log.json` is built with the app, so a sale made in the room cannot
 * be written into it — and it does not need to be. The panels read the log AND the live
 * nominations, and a season that has been marked complete is simply one where the live rows have
 * stopped changing. The nightly job that folds a finished season into the file can happen whenever;
 * the screen does not wait for it.
 *
 * `active` is true because a player won tonight is held tonight, which is what the roster panel
 * counts against the cap.
 */
export function salesTonight(log: readonly FarmNomination[]): FarmRow[] {
  return log
    .filter((n) => n.winner && n.player && n.bid != null)
    .map((n) => ({
      farmYear: n.season,
      gm: n.winner!.toUpperCase(),
      player: n.player!,
      ...(n.position ? { pos: n.position } : {}),
      ...(n.school ? { school: n.school, college: n.school } : {}),
      bid: n.bid ?? null,
      outcome: 'won',
      active: true,
    }));
}

/**
 * Past sales by season, newest first. `who` filters to one manager.
 *
 * `tonight` is merged rather than appended blindly: a season already folded into `farm-log.json`
 * would otherwise appear twice on the evening somebody replays it, so a live row wins over a filed
 * one for the same player and year — the live one is the newer fact.
 */
export function salesByYear(
  who = '',
  tonight: readonly FarmRow[] = [],
): Array<{ year: number; rows: FarmRow[] }> {
  const live = who
    ? tonight.filter((r) => r.gm.toUpperCase() === who.toUpperCase())
    : tonight;
  const claimed = new Set(live.map((r) => `${r.farmYear}|${r.player.toLowerCase()}`));
  const filed = (who
    ? FARM_SALES.filter((r) => r.gm.toUpperCase() === who.toUpperCase())
    : FARM_SALES
  ).filter((r) => !claimed.has(`${r.farmYear}|${r.player.toLowerCase()}`));
  const rows = [...filed, ...live];
  const byYear = new Map<number, FarmRow[]>();
  for (const r of rows) (byYear.get(r.farmYear) ?? byYear.set(r.farmYear, []).get(r.farmYear)!).push(r);
  return [...byYear.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([year, list]) => ({
      year,
      rows: [...list].sort((a, b) => (b.bid ?? 0) - (a.bid ?? 0)),
    }));
}

/** What a GM has paid and what it returned — the reason the filter exists. */
export function spendOf(who: string): { sales: number; paid: number; graduated: number } {
  const rows = FARM_SALES.filter((r) => r.gm.toUpperCase() === who.toUpperCase());
  return {
    sales: rows.length,
    paid: rows.reduce((n, r) => n + (r.bid ?? 0), 0),
    graduated: rows.filter((r) => r.lgRound).length,
  };
}
